import {OidcSecure, useOidc, useOidcAccessToken} from "@axa-fr/react-oidc";
import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo} from "react";
import {apiV2} from "@/api/axiosConfig";
import {useQuery} from "react-query";
import {PermissionsProvider} from "../modulePermissions";
import {ClientModulesProvider} from "../clientModules";
import { registerLogout, registerSessionID } from "@/api/authLogout";

export type AccessTokenPayloadType = {
    iss: string;
    sub: string;
    profile: {
        firstName: string;
        middleNames: string;
        lastName: string;
        dateOfBirth: string;
        gender: string;
        genderAssignedAtBirth: null;
        countryOfResidence: string;
        nationality: string;
        ethnicity: null;
        metadata: null;
    };
    email: string;
    role: {
        id: string;
        name: string;
        description: string;
        auroraModules: string[];
        auroraAccessLevel: "all" | "client" | "department" | "user";
        appPermissions: string[];
        clientID: null;
        clientName: null;
    };
    metadata: {
        clientName: string;
        departmentName: string;
        clientID: string;
        departmentID: string;
    };
    modules: string[];
    iat: number;
    nbf: number;
    exp: number;
};


export const UserContext = createContext<AccessTokenPayloadType | null>(null);


export default function UserContextProvider({
                                                children,
                                            }: {
    children: ReactNode;
}) {
    const {accessTokenPayload} = useOidcAccessToken();

    const data = accessTokenPayload;

    useEffect(() => {
        localStorage.setItem("user", JSON.stringify(data));
    }, []);
    return <UserContext.Provider value={data}>
        <AuthProvider>
            {children}
        </AuthProvider>
    </UserContext.Provider>;
}

export type AuroraAccessLevel = "all" | "client" | "department" | "user"

type AuthContextType = {
    isSuperAdmin: boolean;
    isClientAdmin: boolean;
    isDepartmentAdmin: boolean;
    isUser: boolean;
    clientName: string;
    clientID: string;
    userID: string;
    hasAccessLevel: (accessLevel: AuroraAccessLevel) => boolean;
    isAccessLevel: (accessLevel: AuroraAccessLevel) => boolean;
    hasAccessLevelClient: boolean;
    hasAccessLevelDepartment: boolean;
    isAuthenticated: boolean;
    profile: Profile | undefined;
    refetchProfile: () => void;
    developerAccess: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line @typescript-eslint/ban-types
type AuthProviderProps = {}

type Profile = {
    developerAccess: boolean;
}

async function fetchProfile(): Promise<Profile> {
    const res = await apiV2.get<Profile>("profile")
    return res.data
}

export const AuthProvider: React.FC<React.PropsWithChildren<AuthProviderProps>> = ({children}) => {

    const userContext = useContext(UserContext);
    const {isAuthenticated, logout} = useOidc();
    const { accessTokenPayload } = useOidcAccessToken();

    useEffect(() => {
        registerLogout(logout);
    }, [logout]);

    useEffect(() => {
        registerSessionID((accessTokenPayload as any)?.sid);
    }, [accessTokenPayload]);

    const isSuperAdmin = useMemo(() => {
        return userContext?.role.auroraAccessLevel == "all"
    }, [userContext?.role.auroraAccessLevel]);

    const isUser = useMemo(() => {
        return userContext?.role.auroraAccessLevel == "user"
    }, [userContext?.role.auroraAccessLevel]);

    const isClientAdmin = useMemo(() => {
        return userContext?.role.auroraAccessLevel == "client"
    }, [userContext?.role.auroraAccessLevel]);

    const isDepartmentAdmin = useMemo(() => {
        return userContext?.role.auroraAccessLevel == "department"
    }, [userContext?.role.auroraAccessLevel]);

    const isAccessLevel = useCallback((accessLevel: AuroraAccessLevel) => {
        return userContext?.role.auroraAccessLevel == accessLevel;
    }, [userContext?.role.auroraAccessLevel]);

    const clientName = useMemo(() => {
        return userContext?.metadata.clientName || "";
    }, [userContext?.metadata.clientName]);

    const userID = useMemo(() => {
        return userContext?.sub || "";
    }, [userContext?.sub]);

    const clientID = useMemo(() => {
        return userContext?.metadata.clientID || "";
    }, [userContext?.metadata.clientID]);

    const {data: profile, refetch: refetchProfile} = useQuery(
        ["profile"],
        fetchProfile
    )

    const hasAccessLevel = useCallback((accessLevel: AuroraAccessLevel) => {
        if (!userContext?.role) return false;

        const userAccessLevel = userContext?.role.auroraAccessLevel;

        if (userAccessLevel == "all") return true;

        if (userAccessLevel == "client" && ["client", "department", "user"].includes(accessLevel)) return true
        if (userAccessLevel == "department" && ["department", "user"].includes(accessLevel)) return true
        if (userAccessLevel == "user" && ["user"].includes(accessLevel)) return true

        return false;
    }, [userContext?.role.auroraAccessLevel]);


    const hasAccessLevelClient = useMemo(() => {
        return hasAccessLevel("client");
    }, [hasAccessLevel]);

    const hasAccessLevelDepartment = useMemo(() => {
        return hasAccessLevel("department");
    }, [hasAccessLevel]);

    const developerAccess = useMemo(() => {
        if (!profile) {
            return false;
        }

        return profile.developerAccess;
    }, [profile])

    return <OidcSecure>
        <AuthContext.Provider value={{
            isSuperAdmin,
            isClientAdmin,
            isDepartmentAdmin,
            isUser,
            userID,
            clientID,
            clientName,
            hasAccessLevel,
            isAccessLevel,
            isAuthenticated,
            hasAccessLevelClient,
            hasAccessLevelDepartment,
            profile, refetchProfile,
            developerAccess,

        }}>
            <PermissionsProvider>
                <ClientModulesProvider>
                    {children}
                </ClientModulesProvider>
            </PermissionsProvider>
        </AuthContext.Provider>
    </OidcSecure>
}

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within a AuthProvider");
    }
    return context;
};
